import PrepaidCardApi from '@apis/prepaid/PrepaidCardApi';
import usePrepaidCardPayment from '@hooks/prepaidCard/usePrepaidCardPayment';
import useProjectId from '@hooks/useProjectId';
import { ErrorResponse } from '@models/error';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAtomValue } from 'jotai';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { amountAtom } from '../../../../_shared/atoms/prepaidCardAtoms';
import { nameAtom } from '../../../atoms/purchasePrepaidCardAtoms';

function useOrderPrepaidCard(prepaidCardImageId: string) {
    const { t } = useTranslation('prepaidCard');
    const amount = useAtomValue(amountAtom);
    const cardName = useAtomValue(nameAtom);
    const projectId = useProjectId();
    const openPayment = usePrepaidCardPayment();
    const submitting = useRef(false);
    const { mutate, isPending } = useMutation({
        mutationFn: () => PrepaidCardApi.purchasePrepaidCard({
            projectId,
            prepaidCardImageId,
            cardName,
            amount,
            payMethodType: 'CREDIT_CARD',
        }),
        onSuccess(res) {
            openPayment(res, amount, `${t('image.itemName')} ${amount.toLocaleString()}${t('currencyUnit')}`);
        },
        onError(e: AxiosError<ErrorResponse>) {
            toast.error(e.response?.data?.message || t('image.error.failed'));
        },
        onSettled() {
            submitting.current = false;
        },
    });
    const orderDisabled = isPending || !Number.isSafeInteger(amount) || amount <= 0 || !cardName.trim();

    const handlePurchase = () => {
        if (submitting.current) return;
        if (!Number.isSafeInteger(amount) || amount <= 0) {
            toast.error(t('image.error.enterAmount'));
            return;
        }
        if (!cardName.trim()) {
            toast.error(t('image.error.enterNickname'));
            return;
        }

        submitting.current = true;
        mutate();
    };

    return {
        handlePurchase, amount, orderDisabled,
    };
}

export default useOrderPrepaidCard;
